import { useMutation } from 'react-query'

import { BankLineSubjectAssociationWithSubject } from '@features/bankTransactions/types/bankLineSubjectAssociationWithSubject'
import { BankMatchUiFlowValue, UiSourceValue } from '@features/reconciliation/enums'
import { getBankMatchObjectSource } from '@features/reconciliation/utils'
import { DifferenceType } from '@modules-deprecated/bankReconciliation/types'

import { APIError } from '../../../utils'
import {
  approveBankLineMatch,
  createSubjectAssociation,
  deleteSubjectAssociation,
  ReconcilableResponse,
  ReconciliationSubjectReference,
} from '../query-api'
import { getReferenceFromSubject } from '../utils'

interface UseApproveMatchWithPostingsProps {
  onError?: (error?: APIError) => void
  onSuccess?: (data: ReconcilableResponse) => void
  uiSource?: UiSourceValue
  uiFlow?: BankMatchUiFlowValue
}

export interface ApproveMatchWithPostingsPayload {
  matchId: string
  subjects: ReconciliationSubjectReference[]
  differenceType?: DifferenceType
  isAutoMatch?: boolean
  defaultBankFeeAccountId?: string | null
  existingAssociations?: BankLineSubjectAssociationWithSubject[]
}

export const useApproveMatchWithPostings = (props?: UseApproveMatchWithPostingsProps) => {
  const { onError, onSuccess, uiSource, uiFlow } = props || {}

  const approveMatchWithPostings = async ({
    matchId,
    subjects,
    differenceType,
    isAutoMatch,
    defaultBankFeeAccountId,
    existingAssociations = [],
  }: ApproveMatchWithPostingsPayload): Promise<ReconcilableResponse> => {
    const selectedReferences = new Set(subjects.map((s) => getReferenceFromSubject(s.id, s.type)))

    // Delete all existing associations
    await Promise.all(existingAssociations.map((association) => deleteSubjectAssociation(association.id)))

    // Create new associations for selected subjects, rollback on fail
    const createdIds: string[] = []
    const rollback = () => Promise.all(createdIds.map((id) => deleteSubjectAssociation(id)))

    for (const reference of selectedReferences) {
      try {
        const { bankLineSubjectAssociations } = await createSubjectAssociation(matchId, reference)
        createdIds.push(bankLineSubjectAssociations[0]?.id)
      } catch (error) {
        await rollback()
        throw error
      }
    }

    try {
      return await approveBankLineMatch(
        matchId,
        differenceType,
        defaultBankFeeAccountId,
        isAutoMatch,
        uiSource,
        uiFlow,
        getBankMatchObjectSource(subjects.map((subject) => subject.type)),
      )
    } catch (error) {
      await rollback()
      throw error
    }
  }

  const {
    mutate: approve,
    mutateAsync: approveAsync,
    isLoading: isProcessing,
    ...rest
  } = useMutation(approveMatchWithPostings, {
    onSuccess,
    onError,
  })

  return { approve, approveAsync, isProcessing, ...rest }
}
